import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { DgiiAdminProvider } from '../../contexts/DgiiAdminContext';
import { DgiiCargaProvider } from '../../contexts/DgiiCargaContext';
import {
	HomeIcon,
	DocumentTextIcon,
	ShieldCheckIcon,
	UserGroupIcon,
	ArrowRightOnRectangleIcon,
	Bars3Icon,
	XMarkIcon,
	ChartBarIcon,
	Cog6ToothIcon,
	ChevronLeftIcon,
	ChevronRightIcon,
	PhotoIcon,
	ChatBubbleLeftRightIcon,
	BuildingOfficeIcon,
	PresentationChartBarIcon,
	NewspaperIcon,
	ClipboardDocumentListIcon,
	ArrowTopRightOnSquareIcon,
	ChevronDownIcon,
	ChevronUpIcon,
	DocumentArrowUpIcon,
	ClipboardDocumentCheckIcon
} from '@heroicons/react/24/outline';

const AdminLayout = () => {
	const { user, logout, isAdmin, isGestor } = useAuth();
	const location = useLocation();
	const navigate = useNavigate();
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

	// Cerrar menú móvil al cambiar de ruta
	useEffect(() => {
		setMobileMenuOpen(false);
	}, [location.pathname]);

	const handleLogout = async () => {
		await logout();
		navigate('/login');
	};

	// Estado para controlar qué submenú está expandido
	const [expandedSubmenu, setExpandedSubmenu] = useState(null);

	const menuItems = [
		{
			name: 'Dashboard',
			path: '/admin/dashboard',
			icon: HomeIcon,
			roles: ['admin', 'gestor', 'contador']
		},
		{
			name: 'Gestión de Empresas',
			path: '/admin/empresas',
			icon: BuildingOfficeIcon,
			roles: ['admin', 'gestor', 'contador', 'cliente']
		},
		{
			name: 'Empresas Clientes',
			path: '/admin/gestion-empresas',
			icon: BuildingOfficeIcon,
			roles: ['admin']
		},
		{
			name: 'Empresas en Carpeta',
			path: '/admin/empresas-carpeta',
			icon: BuildingOfficeIcon,
			roles: ['admin']
		},
		{
			name: 'Gestion Documental',
			path: '/admin/documental',
			icon: DocumentTextIcon,
			roles: ['admin']
		},
		{
			name: 'FormsFill',
			path: '/admin/forms-fill',
			icon: DocumentArrowUpIcon,
			roles: ['admin']
		},
		{
			name: 'Actividades Económicas',
			path: '/admin/actividades-economicas',
			icon: ClipboardDocumentListIcon,
			roles: ['admin']
		},
		{
			name: 'Formularios',
			path: '/admin/formularios',
			icon: DocumentTextIcon,
			roles: ['admin', 'gestor', 'contador', 'cliente'],
			submenu: [
				{
					name: 'Carga de Facturas 606',
					path: '/admin/dgii',
					icon: DocumentArrowUpIcon,
					roles: ['admin', 'gestor', 'contador', 'cliente']
				},
			{
				name: 'Gestión 606',
				path: '/admin/dgii/admin',
				icon: ClipboardDocumentCheckIcon,
				roles: ['admin', 'gestor', 'contador']
			},
			{
				name: 'Gestión IR-17',
				path: '/admin/ir-17',
				icon: ChartBarIcon,
				roles: ['admin', 'gestor', 'contador']
			}
			]
		},
		{
			name: 'Banners',
			path: '/admin/banners',
			icon: PhotoIcon,
			roles: ['admin']
		},
		{
			name: 'Testimonios',
			path: '/admin/testimonials',
			icon: ChatBubbleLeftRightIcon,
			roles: ['admin']
		},
		{
			name: 'Partners',
			path: '/admin/partners',
			icon: BuildingOfficeIcon,
			roles: ['admin']
		},
		{
			name: 'Estadísticas',
			path: '/admin/statistics',
			icon: PresentationChartBarIcon,
			roles: ['admin']
		},
		{
			name: 'Blog',
			path: '/admin/blog',
			icon: NewspaperIcon,
			roles: ['admin']
		},
		{
			name: 'Usuarios',
			path: '/admin/users',
			icon: UserGroupIcon,
			roles: ['admin', 'contador']
		},
		{
			name: 'Configuración',
			path: '/admin/settings',
			icon: Cog6ToothIcon,
			roles: ['admin']
		},
		{
			name: 'Auditoría',
			path: '/admin/audit',
			icon: ClipboardDocumentListIcon,
			roles: ['admin']
		}
	];

	const filteredMenuItems = menuItems.filter(item => {
		// Filtrar items principales
		let hasAccess = false;
		if (item.roles.includes('admin') && isAdmin()) hasAccess = true;
		if (item.roles.includes('gestor') && isGestor()) hasAccess = true;
		if (item.roles.includes('contador') && user?.profile?.role === 'contador') hasAccess = true;
		if (item.roles.includes('cliente')) hasAccess = true;
		
		// Si tiene submenú, verificar que al menos un subitem tenga acceso
		if (item.submenu) {
			const hasSubmenuAccess = item.submenu.some(subItem => {
				if (subItem.roles.includes('admin') && isAdmin()) return true;
				if (subItem.roles.includes('gestor') && isGestor()) return true;
				if (subItem.roles.includes('contador') && user?.profile?.role === 'contador') return true;
				if (subItem.roles.includes('cliente')) return true;
				return false;
			});
			return hasAccess && hasSubmenuAccess;
		}
		
		return hasAccess;
	});

	// Verificar si un path está activo (incluyendo submenús)
	const isActive = (path) => {
		// Comparación exacta
		if (location.pathname === path) {
			return true;
		}
		
		// Para sub-rutas, verificar que sea una sub-ruta válida
		// pero solo si no hay otra ruta del menú más específica que también coincida
		if (location.pathname.startsWith(path + '/')) {
			// Buscar si hay otra ruta del menú más específica que también coincide
			const moreSpecificPath = filteredMenuItems.find(item => {
				if (item.path === path) return false; // No comparar consigo mismo
				// Verificar si esta ruta es más específica y también coincide
				return item.path.startsWith(path + '/') && 
				       location.pathname.startsWith(item.path);
			});
			
			// Si hay una ruta más específica que coincide, esta ruta no está activa
			return !moreSpecificPath;
		}
		
		return false;
	};

	// Verificar si un submenú debe estar expandido
	const isSubmenuExpanded = (item) => {
		if (!item.submenu) return false;
		// Expandir si alguna ruta del submenú está activa
		return item.submenu.some(subItem => isActive(subItem.path));
	};

	// Inicializar expandedSubmenu basado en la ruta actual
	useEffect(() => {
		const activeSubmenu = filteredMenuItems.find(item => 
			item.submenu && item.submenu.some(subItem => isActive(subItem.path))
		);
		if (activeSubmenu) {
			setExpandedSubmenu(activeSubmenu.path);
		}
	}, [location.pathname]);

	return (
		<div className="min-h-screen bg-gray-50">
			{/* Sidebar Desktop */}
			<aside 
				className={`hidden lg:flex fixed inset-y-0 left-0 z-40 bg-white border-r border-gray-200 flex-col transition-all duration-300 ease-in-out ${
					sidebarCollapsed ? 'w-20' : 'w-64'
				}`}
			>
				{/* Logo/Header */}
				<div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 text-white" style={{ backgroundColor: '#12284C' }}>
					{!sidebarCollapsed && (
						<Link to="/admin/dashboard" className="flex items-center space-x-2 flex-1">
							<ShieldCheckIcon className="h-8 w-8 flex-shrink-0" />
							<span className="text-xl font-bold whitespace-nowrap">Admin Panel</span>
						</Link>
					)}
					{sidebarCollapsed && (
						<Link to="/admin/dashboard" className="flex items-center justify-center w-full">
							<ShieldCheckIcon className="h-8 w-8" />
						</Link>
					)}
					<button
						onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
						className="ml-2 p-1.5 rounded-md text-white hover:bg-white/20 transition-colors"
						title={sidebarCollapsed ? 'Expandir menú' : 'Colapsar menú'}
					>
						{sidebarCollapsed ? (
							<ChevronRightIcon className="h-5 w-5" />
						) : (
							<ChevronLeftIcon className="h-5 w-5" />
						)}
					</button>
				</div>

				{/* User Info */}
				{!sidebarCollapsed && (
					<div className="px-4 py-4 border-b border-gray-200">
						<div className="flex items-center space-x-3">
							<div className="h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(18, 40, 76, 0.1)' }}>
								<span className="font-semibold" style={{ color: '#12284C' }}>
									{user?.first_name?.[0] || user?.username?.[0] || 'U'}
								</span>
							</div>
							<div className="flex-1 min-w-0">
								<p className="text-sm font-medium text-gray-900 truncate">
									{user?.first_name && user?.last_name
										? `${user.first_name} ${user.last_name}`
										: user?.username || 'Usuario'}
								</p>
								<p className="text-xs text-gray-500 truncate">
									{user?.userprofile?.role === 'admin' && 'Administrador'}
									{user?.userprofile?.role === 'gestor' && 'Gestor'}
									{user?.userprofile?.role === 'cliente' && 'Cliente'}
								</p>
							</div>
						</div>
					</div>
				)}
				{sidebarCollapsed && (
					<div className="px-2 py-4 border-b border-gray-200 flex justify-center">
						<div className="h-10 w-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(18, 40, 76, 0.1)' }}>
							<span className="font-semibold" style={{ color: '#12284C' }}>
								{user?.first_name?.[0] || user?.username?.[0] || 'U'}
							</span>
						</div>
					</div>
				)}

				{/* Navigation */}
				<nav className="flex-1 overflow-y-auto px-2 py-4 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
					<div className="space-y-1">
						{filteredMenuItems.map((item) => {
							const Icon = item.icon;
							const hasSubmenu = item.submenu && item.submenu.length > 0;
							const isExpanded = expandedSubmenu === item.path;
							const submenuActive = hasSubmenu && isSubmenuExpanded(item);
							
							// Si tiene submenú, renderizar como botón desplegable
							if (hasSubmenu && !sidebarCollapsed) {
								const filteredSubmenu = item.submenu.filter(subItem => {
									if (subItem.roles.includes('admin') && isAdmin()) return true;
									if (subItem.roles.includes('gestor') && isGestor()) return true;
									if (subItem.roles.includes('contador') && user?.profile?.role === 'contador') return true;
									if (subItem.roles.includes('cliente')) return true;
									return false;
								});

								return (
									<div key={item.path} className="space-y-1">
										<button
											onClick={() => setExpandedSubmenu(isExpanded ? null : item.path)}
											className={`
												group flex items-center justify-between w-full space-x-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 relative
												${submenuActive
													? 'text-white shadow-md'
													: 'text-gray-700 hover:bg-gray-100'
												}
											`}
											style={submenuActive ? { backgroundColor: '#12284C', color: '#ffffff' } : {}}
										>
											<div className="flex items-center space-x-3 flex-1">
												<Icon className={`h-5 w-5 flex-shrink-0 ${submenuActive ? 'scale-110 text-white' : ''} transition-transform duration-200`} style={submenuActive ? { color: '#ffffff' } : {}} />
												<span className={`whitespace-nowrap ${submenuActive ? 'text-white' : ''}`} style={submenuActive ? { color: '#ffffff' } : {}}>{item.name}</span>
											</div>
											{isExpanded ? (
												<ChevronUpIcon className="h-4 w-4 flex-shrink-0" style={submenuActive ? { color: '#ffffff' } : {}} />
											) : (
												<ChevronDownIcon className="h-4 w-4 flex-shrink-0" style={submenuActive ? { color: '#ffffff' } : {}} />
											)}
										</button>
										{isExpanded && (
											<div className="ml-4 space-y-1 border-l-2 border-gray-200 pl-2">
												{filteredSubmenu.map((subItem) => {
													const SubIcon = subItem.icon;
													const subActive = isActive(subItem.path);
													return (
														<Link
															key={subItem.path}
															to={subItem.path}
															className={`
																group flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
																${subActive
																	? 'text-white shadow-md'
																	: 'text-gray-600 hover:bg-gray-100'
																}
															`}
															style={subActive ? { backgroundColor: '#12284C', color: '#ffffff' } : {}}
														>
															<SubIcon className={`h-4 w-4 flex-shrink-0 ${subActive ? 'text-white' : ''}`} style={subActive ? { color: '#ffffff' } : {}} />
															<span className={`whitespace-nowrap ${subActive ? 'text-white' : ''}`} style={subActive ? { color: '#ffffff' } : {}}>{subItem.name}</span>
														</Link>
													);
												})}
											</div>
										)}
									</div>
								);
							}
							
							// Si no tiene submenú o el sidebar está colapsado, renderizar como link normal
							const active = isActive(item.path);
							return (
								<Link
									key={item.path}
									to={item.path}
									className={`
										group flex items-center ${sidebarCollapsed ? 'justify-center' : 'space-x-3'} ${active && !sidebarCollapsed ? 'pl-6' : 'px-3'} py-2.5 rounded-lg text-sm font-medium transition-all duration-200 relative
										${active
											? 'text-white shadow-md'
											: 'text-gray-700 hover:bg-gray-100'
										}
									`}
									style={active ? { backgroundColor: '#12284C', color: '#ffffff' } : {}}
									onMouseEnter={(e) => {
										if (!active) {
											e.currentTarget.style.color = '#12284C';
											e.currentTarget.style.backgroundColor = 'rgba(18, 40, 76, 0.1)';
										}
									}}
									onMouseLeave={(e) => {
										if (!active) {
											e.currentTarget.style.color = '';
											e.currentTarget.style.backgroundColor = '';
										} else {
											// Asegurar que el texto siga siendo blanco cuando está activo
											e.currentTarget.style.color = '#ffffff';
										}
									}}
									title={sidebarCollapsed ? item.name : ''}
								>
									<Icon className={`h-5 w-5 flex-shrink-0 ${active ? 'scale-110 text-white' : ''} transition-transform duration-200`} style={active ? { color: '#ffffff' } : {}} />
									{!sidebarCollapsed && (
										<span className={`whitespace-nowrap ${active ? 'text-white' : ''}`} style={active ? { color: '#ffffff' } : {}}>{item.name}</span>
									)}
									{active && !sidebarCollapsed && (
										<div className="absolute left-1 top-1 bottom-1 w-0.5 bg-white rounded-r-full"></div>
									)}
									{sidebarCollapsed && active && (
										<div className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-white rounded-l-full"></div>
									)}
								</Link>
							);
						})}
					</div>
				</nav>

				{/* Página Principal y Logout */}
				<div className={`px-4 py-4 border-t border-gray-200 ${sidebarCollapsed ? 'px-2' : ''} space-y-2`}>
					<Link
						to="/"
						className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'space-x-3'} w-full px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-all duration-200`}
						title={sidebarCollapsed ? 'Página Principal' : ''}
					>
						<ArrowTopRightOnSquareIcon className="h-5 w-5 flex-shrink-0" />
						{!sidebarCollapsed && <span>Página Principal</span>}
					</Link>
					<button
						onClick={handleLogout}
						className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'space-x-3'} w-full px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-red-50 hover:text-red-600 transition-all duration-200`}
						title={sidebarCollapsed ? 'Cerrar Sesión' : ''}
					>
						<ArrowRightOnRectangleIcon className="h-5 w-5 flex-shrink-0" />
						{!sidebarCollapsed && <span>Cerrar Sesión</span>}
					</button>
				</div>
			</aside>

			{/* Mobile menu button */}
			<div className="lg:hidden fixed top-4 left-4 z-40">
				<button
					onClick={() => setMobileMenuOpen(true)}
					className="p-2.5 rounded-lg bg-white shadow-lg text-gray-700 hover:bg-gray-100 active:scale-95 transition-all duration-200"
					aria-label="Abrir menú"
				>
					<Bars3Icon className="h-6 w-6" />
				</button>
			</div>

			{/* Mobile sidebar overlay */}
			{mobileMenuOpen && (
				<>
					<div
						className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden animate-in fade-in duration-200"
						onClick={() => setMobileMenuOpen(false)}
					/>
					<aside className="fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 flex flex-col lg:hidden transform transition-transform duration-300 ease-in-out shadow-xl">
						{/* Same sidebar content for mobile */}
						<div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 text-white" style={{ backgroundColor: '#12284C' }}>
							<Link to="/admin/dashboard" className="flex items-center space-x-2">
								<ShieldCheckIcon className="h-8 w-8" />
								<span className="text-xl font-bold">Admin Panel</span>
							</Link>
							<button
								onClick={() => setMobileMenuOpen(false)}
								className="text-white hover:text-gray-200"
							>
								<XMarkIcon className="h-6 w-6" />
							</button>
						</div>

						<div className="px-4 py-4 border-b border-gray-200">
							<div className="flex items-center space-x-3">
								<div className="h-10 w-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(18, 40, 76, 0.1)' }}>
									<span className="font-semibold" style={{ color: '#12284C' }}>
										{user?.first_name?.[0] || user?.username?.[0] || 'U'}
									</span>
								</div>
								<div className="flex-1 min-w-0">
									<p className="text-sm font-medium text-gray-900 truncate">
										{user?.first_name && user?.last_name
											? `${user.first_name} ${user.last_name}`
											: user?.username || 'Usuario'}
									</p>
									<p className="text-xs text-gray-500 truncate">
										{user?.userprofile?.role === 'admin' && 'Administrador'}
										{user?.userprofile?.role === 'gestor' && 'Gestor'}
										{user?.userprofile?.role === 'cliente' && 'Cliente'}
									</p>
								</div>
							</div>
						</div>

						<nav className="flex-1 overflow-y-auto px-2 py-4">
							<div className="space-y-1">
								{filteredMenuItems.map((item) => {
									const Icon = item.icon;
									const hasSubmenu = item.submenu && item.submenu.length > 0;
									const isExpanded = expandedSubmenu === item.path;
									const submenuActive = hasSubmenu && isSubmenuExpanded(item);
									
									// Si tiene submenú, renderizar como botón desplegable
									if (hasSubmenu) {
										const filteredSubmenu = item.submenu.filter(subItem => {
											if (subItem.roles.includes('admin') && isAdmin()) return true;
											if (subItem.roles.includes('gestor') && isGestor()) return true;
											if (subItem.roles.includes('contador') && user?.profile?.role === 'contador') return true;
											if (subItem.roles.includes('cliente')) return true;
											return false;
										});

										return (
											<div key={item.path} className="space-y-1">
												<button
													onClick={() => setExpandedSubmenu(isExpanded ? null : item.path)}
													className={`
														group flex items-center justify-between w-full space-x-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 relative
														${submenuActive
															? 'text-white shadow-md'
															: 'text-gray-700 hover:bg-gray-100'
														}
													`}
													style={submenuActive ? { backgroundColor: '#12284C', color: '#ffffff' } : {}}
												>
													<div className="flex items-center space-x-3 flex-1">
														<Icon className={`h-5 w-5 flex-shrink-0 ${submenuActive ? 'scale-110 text-white' : ''} transition-transform duration-200`} style={submenuActive ? { color: '#ffffff' } : {}} />
														<span className={`whitespace-nowrap ${submenuActive ? 'text-white' : ''}`} style={submenuActive ? { color: '#ffffff' } : {}}>{item.name}</span>
													</div>
													{isExpanded ? (
														<ChevronUpIcon className="h-4 w-4 flex-shrink-0" style={submenuActive ? { color: '#ffffff' } : {}} />
													) : (
														<ChevronDownIcon className="h-4 w-4 flex-shrink-0" style={submenuActive ? { color: '#ffffff' } : {}} />
													)}
												</button>
												{isExpanded && (
													<div className="ml-4 space-y-1 border-l-2 border-gray-200 pl-2">
														{filteredSubmenu.map((subItem) => {
															const SubIcon = subItem.icon;
															const subActive = isActive(subItem.path);
															return (
																<Link
																	key={subItem.path}
																	to={subItem.path}
																	onClick={() => setMobileMenuOpen(false)}
																	className={`
																		group flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
																		${subActive
																			? 'text-white shadow-md'
																			: 'text-gray-600 hover:bg-gray-100'
																		}
																	`}
																	style={subActive ? { backgroundColor: '#12284C', color: '#ffffff' } : {}}
																>
																	<SubIcon className={`h-4 w-4 flex-shrink-0 ${subActive ? 'text-white' : ''}`} style={subActive ? { color: '#ffffff' } : {}} />
																	<span className={`whitespace-nowrap ${subActive ? 'text-white' : ''}`} style={subActive ? { color: '#ffffff' } : {}}>{subItem.name}</span>
																</Link>
															);
														})}
													</div>
												)}
											</div>
										);
									}
									
									// Si no tiene submenú, renderizar como link normal
									const active = isActive(item.path);
									return (
										<Link
											key={item.path}
											to={item.path}
											onClick={() => setMobileMenuOpen(false)}
											className={`
												group flex items-center space-x-3 ${active ? 'pl-6' : 'px-3'} py-2.5 rounded-lg text-sm font-medium transition-all duration-200 relative
												${active
													? 'text-white shadow-md'
													: 'text-gray-700 hover:bg-gray-100'
												}
											`}
											style={active ? { backgroundColor: '#12284C' } : {}}
											onMouseEnter={(e) => {
												if (!active) {
													e.currentTarget.style.color = '#12284C';
													e.currentTarget.style.backgroundColor = 'rgba(18, 40, 76, 0.1)';
												}
											}}
											onMouseLeave={(e) => {
												if (!active) {
													e.currentTarget.style.color = '';
													e.currentTarget.style.backgroundColor = '';
												}
											}}
										>
											<Icon className={`h-5 w-5 flex-shrink-0 ${active ? 'text-white scale-110' : ''} transition-transform duration-200`} style={active ? { color: '#ffffff' } : {}} />
											<span className={`whitespace-nowrap ${active ? 'text-white' : ''}`} style={active ? { color: '#ffffff' } : {}}>{item.name}</span>
											{active && (
												<div className="absolute left-1 top-1 bottom-1 w-0.5 bg-white rounded-r-full"></div>
											)}
										</Link>
									);
								})}
							</div>
						</nav>

						<div className="px-4 py-4 border-t border-gray-200 space-y-2">
							<Link
								to="/"
								onClick={() => setMobileMenuOpen(false)}
								className="flex items-center space-x-3 w-full px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
							>
								<ArrowTopRightOnSquareIcon className="h-5 w-5" />
								<span>Página Principal</span>
							</Link>
							<button
								onClick={handleLogout}
								className="flex items-center space-x-3 w-full px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors"
							>
								<ArrowRightOnRectangleIcon className="h-5 w-5" />
								<span>Cerrar Sesión</span>
							</button>
						</div>
					</aside>
				</>
			)}

			{/* Main content - DgiiAdminProvider persiste stats al cambiar de menú */}
			<div className={`transition-all duration-300 ${sidebarCollapsed ? 'lg:pl-20' : 'lg:pl-64'}`}>
				<main className="py-6 pt-20 lg:pt-6">
					<DgiiAdminProvider>
						<DgiiCargaProvider>
							<Outlet />
						</DgiiCargaProvider>
					</DgiiAdminProvider>
				</main>
			</div>
		</div>
	);
};

export default AdminLayout;

